import { isRecord } from "./type";

export const isJson = (text: string): boolean => {
  text = text.trim();
  if (text.startsWith("{") && text.endsWith("}")) {
    try {
      JSON.parse(text);
      return true;
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return false;
    }
  }
  return false;
};

const isObject = (value: unknown): value is object =>
  typeof value === "object" && value !== null;

export const asJsonObjArray = (text: string): object[] | undefined => {
  text = text.trim();
  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const arr: unknown = JSON.parse(text);
      if (Array.isArray(arr) && arr.every(isObject)) {
        return arr;
      } else {
        return undefined;
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const parsedJson = (text: string): unknown => {
  text = text.trim();
  if (text.startsWith("{") && text.endsWith("}")) {
    try {
      return JSON.parse(text);
    } catch {
      return undefined;
    }
  }
  return undefined;
};

/**
 * Parses `text` as a JSON object, returning `undefined` for anything else
 * (invalid JSON, arrays, scalars). Validates and parses the same trimmed
 * string, so callers never need a separate `isJson` check that could
 * disagree with the parse (`trim()` strips whitespace JSON.parse rejects).
 */
export const parseJsonRecord = (
  text: string
): Record<string, unknown> | undefined => {
  const parsed = parsedJson(text);
  return isRecord(parsed) ? parsed : undefined;
};

// Estimates the size of a list of objects by sampling a subset of the list.
export function estimateSize(list: unknown[], frequency = 0.2) {
  if (list.length === 0) {
    return 0;
  }

  // Total number of samples
  const sampleSize = Math.ceil(list.length * frequency);

  // Get a proper random sample without duplicates
  const messageIndices = new Set<number>();
  while (
    messageIndices.size < sampleSize &&
    messageIndices.size < list.length
  ) {
    const randomIndex = Math.floor(Math.random() * list.length);
    messageIndices.add(randomIndex);
  }

  // Calculate size from sampled messages
  const totalSize = Array.from(messageIndices).reduce((size, index) => {
    return size + JSON.stringify(list[index]).length;
  }, 0);

  // Estimate total size based on sample
  const estimatedTotalSize = (totalSize / sampleSize) * list.length;
  return estimatedTotalSize;
}
